import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { COMMON_API } from '../../CommonAPI';

const Projects = () => {
    const [projects, setProjects] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");

    const projectsPerPage = 10;

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await axios.get(`${COMMON_API}/api/project/get-projects`);
                setProjects(response.data);
            } catch (error) {
                console.error("Error fetching projects:", error);
                toast.error("Error fetching projects!");
            }
        };
        fetchProjects();
    }, []);

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this project?");
        if (!confirmDelete) return;

        try {
            const token = sessionStorage.getItem("authToken");
            await axios.delete(`${COMMON_API}/api/project/delete-project/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Project deleted successfully!");
            setProjects(prev => prev.filter(project => project.id !== id));
        } catch (error) {
            console.error("Error deleting project:", error);
            toast.error("Failed to delete project!");
        }
    };

    const filteredProjects = projects.filter(project =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);
    const indexOfLastProject = currentPage * projectsPerPage;
    const indexOfFirstProject = indexOfLastProject - projectsPerPage;
    const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);

    return (
        <div className="ml-64 p-8 bg-gray-100 min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                    <input
                        type="text"
                        placeholder="Search by project name..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                    />
                </div>

                <Link
                    to="/add_project"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                >
                    Add Project
                </Link>
            </div>

            <div className="overflow-x-auto bg-white rounded shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">S.No</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categories</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Images</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {currentProjects.length > 0 ? (
                            currentProjects.map((project, index) => (
                                <tr key={project.id}>
                                    <td className="px-6 py-4">{indexOfFirstProject + index + 1}</td>
                                    <td className="px-6 py-4">{project.title}</td>
                                    <td className="px-6 py-4">
                                        {project.categories.map((cat, i) => (
                                            <span 
                                                key={i} 
                                                className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded"
                                            >
                                                {cat}
                                            </span>
                                        ))}
                                    </td>
                                    <td className="px-6 py-4">
                                        {project.images.length > 0 ? (
                                            <div className="flex gap-3 flex-wrap">
                                                {project.images.map((img, i) => (
                                                    <div key={i} className="flex flex-col items-center">
                                                        <img
                                                            src={`${COMMON_API}/api/upload/${img.file}`}
                                                            alt={img.type}
                                                            className="w-24 h-16 object-cover rounded mb-1"
                                                        />
                                                        <span className="text-xs text-gray-600 capitalize">
                                                            {img.type}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-500 text-sm">No Images</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 flex gap-3">
                                        <Link
                                            to={`/edit_project/${project.id}`}
                                            className="bg-yellow-400 hover:bg-yellow-500 text-white px-3 py-1 rounded"
                                        >
                                            Edit
                                        </Link>
                                        <button
                                            onClick={() => handleDelete(project.id)}
                                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center text-gray-500 py-4">
                                    No projects found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {filteredProjects.length > 0 && (
                <div className="flex justify-center items-center mt-6 space-x-2">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded ${currentPage === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                    >
                        Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentPage(index + 1)}
                            className={`px-3 py-1 rounded ${currentPage === index + 1 ? 'bg-blue-700 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                        >
                            {index + 1}
                        </button>
                    ))}
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded ${currentPage === totalPages ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default Projects;
